"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { Invoice } from "@/types";
import { invoiceService } from "@/services/invoiceService";
import { useCustomers } from "./CustomerContext";
import { useActivities } from "./ActivityContext";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

interface InvoiceContextType {
  invoices: Invoice[];
  loading: boolean;
  error: string | null;
  addInvoice: (invoice: Omit<Invoice, "id">) => Invoice;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => void;
  deleteInvoice: (id: string) => void;
  getInvoiceById: (id: string) => Invoice | undefined;
  fetchInvoices: () => Promise<void>;
  refreshInvoices: () => Promise<void>;
}

const InvoiceContext = createContext<InvoiceContextType | undefined>(undefined);

export function InvoiceProvider({ children }: { children: ReactNode }) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { customers } = useCustomers();
  const { addActivity } = useActivities();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const fetchInvoices = useCallback(async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await invoiceService.getAllInvoices();
      setInvoices(data);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch invoices";
      setError(errorMessage);
      console.error("Error fetching invoices:", error);
      // Suppress toast.error() to prevent UI interruption due to missing backend API
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refreshInvoices = useCallback(async () => {
    await fetchInvoices();
  }, [fetchInvoices]);

  const addInvoice = useCallback(
    (invoiceData: Omit<Invoice, "id">) => {
      // Create it upfront so we can reference it safely
      const newInvoice: Invoice = {
        ...invoiceData,
        id: `INV${String(invoices.length + 1).padStart(4, "0")}`,
      };

      setInvoices((prev) => [newInvoice, ...prev]);

      if (invoiceData.paymentStatus === "paid") {
        const customer = customers.find((c) => c.id === invoiceData.customerId);
        if (customer) {
          addActivity({
            userId: "SYSTEM",
            action: "received",
            module: "payment",
            recordId: newInvoice.id,
            description: `Payment received: ${invoiceData.total} QAR from ${customer.name}`,
          });
        }
      }

      return newInvoice;
    },
    [invoices.length, customers, addActivity]
  );

  const updateInvoice = useCallback(
    (id: string, invoiceData: Partial<Invoice>) => {
      setInvoices((prev) =>
        prev.map((i) => (i.id === id ? { ...i, ...invoiceData } : i))
      );
    },
    []
  );

  const deleteInvoice = useCallback((id: string) => {
    setInvoices((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const getInvoiceById = useCallback(
    (id: string) => {
      return invoices.find((i) => i.id === id);
    },
    [invoices]
  );

  // Fetch invoices on mount, but only if authenticated
  useEffect(() => {
    // Wait for auth to finish loading, then check if authenticated
    if (!authLoading && isAuthenticated) {
      fetchInvoices();
    }
  }, [fetchInvoices, authLoading, isAuthenticated]);

  const value: InvoiceContextType = {
    invoices,
    loading,
    error,
    addInvoice,
    updateInvoice,
    deleteInvoice,
    getInvoiceById,
    fetchInvoices,
    refreshInvoices,
  };

  return <InvoiceContext.Provider value={value}>{children}</InvoiceContext.Provider>;
}

export function useInvoices() {
  const context = useContext(InvoiceContext);
  if (context === undefined) {
    throw new Error("useInvoices must be used within an InvoiceProvider");
  }
  return context;
}

